// EXTERNAL DEPENDENCIES
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

// COMPONENTS
import ButtonWithState from '@/common/components/ui/buttons/button-with-state'

// HOOKS
import { useMutationCheck } from '@/hooks'
import {
  useBusinessProfilePageContent,
  useBusinessProfileReduxState,
  useModularSellerViewPageContent,
  useSaveChanges,
} from '@/screens/business-profile-page/hooks'

// REDUX
import { updateEditPageErrorMessage } from '@/redux/features'

// TYPES
import { ProfileData, UnformattedFptiOptions } from '@/types'

// STYLES
import styles from './styles.module.less'

/**
 * Displays a button that handles saving business info edits
 */
export function SaveEditsButton({
  canSave,
  successfulUpdateToastMessage,
  failedUpdateErrorMessage,
  successPublishFpti,
  saveButtonFpti,
  onSuccess,
  onError,
}: Readonly<{
  canSave: boolean
  successfulUpdateToastMessage: string
  failedUpdateErrorMessage?: string
  successPublishFpti?: UnformattedFptiOptions
  saveButtonFpti?: string
  onSuccess?: () => void
  handleLoading?: (isLoading: boolean) => void
  onError?: () => void
}>) {
  const isValidating = useMutationCheck()
  const dispatch = useDispatch()

  // STATE
  const [isClicked, setIsClicked] = useState(false)

  // INITIALIZE CONTENT
  const getModularContent = useModularSellerViewPageContent()
  const getBusinessProfileContent = useBusinessProfilePageContent()
  const saveButtonText = getModularContent('saveButton')
  const generalProfileUpdateErrorMessage = getModularContent(
    'generalProfileUpdateError',
  )
  const unableToSaveWhenErrorsText = getBusinessProfileContent(
    'saveWithExistingErrorToast',
  )
  // HOOKS
  const saveEdits = useSaveChanges({
    successMessage: successfulUpdateToastMessage,
    errorMessage: failedUpdateErrorMessage || generalProfileUpdateErrorMessage,
    onSuccess,
    onError,
  })

  // STATE
  const {
    saveStatus,
    editedProfileData: {
      values: {
        coverPhoto,
        profilePhoto,
        businessDescription,
        contactInfo = {},
        profilePreferences,
      },
    },
  } = useBusinessProfileReduxState()

  const handleSave = useCallback(async () => {
    if (!isValidating && (canSave || canSave === null)) {
      dispatch(updateEditPageErrorMessage(''))

      const data = {
        coverPhoto,
        profilePhoto,
        businessDescription,
        selectedContactInfo: contactInfo,
        selectedLocationDisplayPreference:
          profilePreferences.locationDisplayPreference,
      } as ProfileData

      await saveEdits(data, { success: successPublishFpti })
    }
  }, [
    businessDescription,
    canSave,
    contactInfo,
    coverPhoto,
    dispatch,
    isValidating,
    profilePhoto,
    profilePreferences.locationDisplayPreference,
    saveEdits,
    successPublishFpti,
  ])

  useEffect(() => {
    if (canSave === false && isClicked) {
      dispatch(updateEditPageErrorMessage(unableToSaveWhenErrorsText))
    }
  }, [canSave, isClicked, unableToSaveWhenErrorsText, dispatch])

  return (
    <ButtonWithState
      className={styles.saveButton}
      onClick={handleSave}
      data-fpti={saveButtonFpti}
      canProceed={canSave}
      shouldShowSpinner={saveStatus === 'saving' || saveStatus === 'success'}
      updateClickStatus={setIsClicked}
    >
      {saveButtonText}
    </ButtonWithState>
  )
}
